import { ColumnMappingBaseTransformPlainObject, RuleBasedMappingPlainObject } from '../plainObjects/columnMappingBase.plainObject';
import { dataFlowPatternParameter } from '../constants';
import { SchemaModifierBasePlainObject } from '../plainObjects/schemaModifierBase.plainObject';
import { ExpressionColumnPatternPlainObject, ExpressionColumnPlainObject } from '../plainObjects/expressionColumn.plainObject';
import { Antlr4Utils } from '../models/antlr4-utils';
import { GroupByTransformPlainObject } from '../plainObjects/groupByTransform.plainObject';
import { SchemaUtil } from './schema.util';
import { PivotBasePlainObject } from '../plainObjects/pivotBase.plainObject';
import { ParsePlainObject } from '../plainObjects/parse.plainObject';
import { ExecuteFlowletPlainObject } from '../plainObjects/executeFlowlet.plainObject';
import { ParameterValuePlainObject } from '../plainObjects/parameter.plainObject';

/* eslint-disable @typescript-eslint/no-explicit-any */
export class MatchFunctionUtil {
    public static addColumnPatternColumns(transformation: SchemaModifierBasePlainObject, tokenStream: any, context: any): void {
        const derivedColumnPattern = new ExpressionColumnPatternPlainObject();
        transformation.columns.push(derivedColumnPattern);

        const metaFunction = context.metafunction();
        derivedColumnPattern.condition = MatchFunctionUtil._getTemplateExpression(metaFunction);
        derivedColumnPattern.hierarchyLevelExpression = MatchFunctionUtil._getEachFnLevel(metaFunction);
        derivedColumnPattern.unEscapedRegexString = MatchFunctionUtil._getRegexString(metaFunction);

        // update all columns
        const metaAssignments = metaFunction.metaAssignment();
        if (metaAssignments) {
            for (const metaAssignment of metaAssignments) {
                const derivedColumn = new ExpressionColumnPlainObject();
                derivedColumnPattern.columns.push(derivedColumn);
                const metaAssignmentExpressions = metaAssignment.expression();
                derivedColumn.name = metaAssignmentExpressions[0] ? Antlr4Utils.getText(tokenStream, metaAssignmentExpressions[0]) : '';

                if (transformation instanceof ParsePlainObject && metaAssignmentExpressions[1]) {
                    // in the case of parse transformation, a column pattern will have an output type expression,
                    // each sub column will be casted (represented by a question mark in the dsl) to the output type
                    derivedColumn.expression = Antlr4Utils.getText(tokenStream, metaAssignmentExpressions[1].left);
                    derivedColumnPattern.typeExpression = Antlr4Utils.getText(tokenStream, metaAssignmentExpressions[1].right);
                } else {
                    derivedColumn.expression = metaAssignmentExpressions[1] ? Antlr4Utils.getText(tokenStream, metaAssignmentExpressions[1]) : '';
                }
            }
        }

        // special case where the name of the column is a parameter operand
        const assignments = metaFunction.assignment();
        if (assignments) {
            for (const assignment of assignments) {
                const derivedColumn = new ExpressionColumnPlainObject();
                derivedColumnPattern.columns.push(derivedColumn);

                const paramOperand = assignment.parameterOperand();
                const assignmentExpr = assignment.expression();
                derivedColumn.name = paramOperand ? Antlr4Utils.getText(tokenStream, paramOperand) : '';
                derivedColumn.expression = derivedColumn.name && assignmentExpr ? Antlr4Utils.getText(tokenStream, assignmentExpr) : '';
            }
        }
    }

    public static addGroupByColumns(transformation: GroupByTransformPlainObject, expressions: any): void {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        expressions.children.forEach((expression: any) => {
            if (expression.columnOperand && expression.columnOperand()) {
                const expressionText = expression.columnOperand().getText();
                MatchFunctionUtil._addGroupByColumn(transformation, expressionText);
            } else if (expression.ASSIGN && expression.ASSIGN()) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                MatchFunctionUtil._addGroupByColumn(transformation,
                    SchemaUtil.getColumnName(expression.columnIdentifier()),
                    expression.expression().getText());
            }
        });
    }

    public static addExpressionBasedMappingColumnFromMetaFunctionExpr(transformation: ColumnMappingBaseTransformPlainObject, context: any, deepHierarchy: boolean, rowIndex: number = 0): void {
        // each(match(type=='string'), abc = $$)
        const templateCondition = MatchFunctionUtil._getTemplateExpression(context.metafunction());
        const hierarchyLevelExpression = MatchFunctionUtil._getEachFnLevel(context.metafunction());
        const regexString = MatchFunctionUtil._getRegexString(context.metafunction());

        const metaAssignments = context.metafunction().metaAssignment();
        const assignments = context.metafunction().assignment();
        if (metaAssignments && metaAssignments.length) {
            for (const metaAssignment of metaAssignments) {
                const columnNameExpression = metaAssignment.expression()[0] ? metaAssignment.expression()[0].getText() : '';
                MatchFunctionUtil._addRuleBasedMapping(transformation, templateCondition, columnNameExpression, rowIndex, regexString, hierarchyLevelExpression, deepHierarchy);
            }
            // for parameter scenario $p1 = $$
        } else if (assignments && assignments.length) {
            for (const assignment of assignments) {
                const columnNameExpression = assignment.parameterOperand() ? assignment.parameterOperand().getText() : '';
                MatchFunctionUtil._addRuleBasedMapping(transformation, templateCondition, columnNameExpression, rowIndex, regexString, hierarchyLevelExpression, deepHierarchy);
            }
        }
    }

    public static addExpressionBasedMappingColumnFromFunctionExpr(transformation: ColumnMappingBaseTransformPlainObject, context: any, deepHierarchy: boolean, rowIndex: number = 0): void {
        // each(match(type=='string'), $$)
        const templateCondition = MatchFunctionUtil._getTemplateExpression(context.basicFunction());
        const hierarchyLevelExpression = MatchFunctionUtil._getEachFnLevel(context.basicFunction());
        const columnNameExpression = MatchFunctionUtil._getColumnNameExpression(context.basicFunction());
        const regexString = MatchFunctionUtil._getRegexString(context.basicFunction());

        MatchFunctionUtil._addRuleBasedMapping(transformation, templateCondition, columnNameExpression, rowIndex, regexString, hierarchyLevelExpression, deepHierarchy);
    }

    public static addPivotValues(transformation: PivotBasePlainObject, context: any): void {
        const valueExpressions = context.expression();
        if (valueExpressions) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            valueExpressions.forEach((value: any) => {
                const val = value.getText();
                if (val !== 'null') {
                    transformation.pivotKeyValues.push(val);
                } else {
                    transformation.nullValue = true;
                }
            });
        }
    }

    public static addParameterValues(transformation: ExecuteFlowletPlainObject, mapParametersFunction: any): void {
        const assignments = mapParametersFunction.assignment();
        for (const assignment of assignments) {
            const parameterValue = new ParameterValuePlainObject();
            parameterValue.name = assignment.columnIdentifier() ? assignment.columnIdentifier().getText() : '';
            parameterValue.value = assignment.expression().getText();
            transformation.parameters.push(parameterValue);
        }
    }

    private static _addGroupByColumn(transformation: GroupByTransformPlainObject, name: string, expression?: string): void {
        const groupByColumn = new ExpressionColumnPlainObject();
        groupByColumn.name = name;
        groupByColumn.expression = expression;
        transformation.groupByColumns.push(groupByColumn);
    }

    private static _addRuleBasedMapping(transformation: ColumnMappingBaseTransformPlainObject, templateCondition: string, columnNameExpression: string,
        rowIndex: number, regexString?: string, hierarchyLevelExpression?: string, deepHierarchy?: boolean): void {
        const rule = new RuleBasedMappingPlainObject();
        rule.unEscapedRegexString = regexString;
        rule.templateCondition = templateCondition;
        rule.columnNameExpression = columnNameExpression;
        rule.hierarchyLevelExpression = hierarchyLevelExpression;
        rule.deepHierarchy = deepHierarchy;
        rule.rowIndex = rowIndex;
        transformation.rules.push(rule);
    }

    private static _getTemplateExpression(eachFn: any): string {
        const matchFunctionName = MatchFunctionUtil._getMatchFunctionName(eachFn);
        const functionExpressions = this._getFunctionExpressionsFromEachFunction(eachFn);
        return (matchFunctionName === 'patternMatch' ? functionExpressions[1] : functionExpressions[0]) || '';
    }

    private static _getRegexString(eachFn: any): string {
        const matchFunctionName = MatchFunctionUtil._getMatchFunctionName(eachFn);
        return matchFunctionName === 'patternMatch' && this._getFunctionExpressionsFromEachFunction(eachFn)[0] || '';
    }

    private static _getFunctionExpressionsFromEachFunction(eachFn: any): string[] {
        const expression = MatchFunctionUtil._getMatchFunctionExpressions(eachFn);
        const functionExpressions = expression ? expression.basicFunction().expression() : [''];
        return functionExpressions.map((functionExpression: any) => functionExpression ? functionExpression.getText() : '');
    }

    private static _getMatchFunctionName(eachFn: any): string {
        const expression = MatchFunctionUtil._getMatchFunctionExpressions(eachFn);
        return expression ? expression.basicFunction().basicName().getText() : '';
    }

    private static _getMatchFunctionExpressions(eachFn: any): any {
        const expressions = eachFn.expression();
        for (const exp of expressions) {
            if (exp.basicFunction && (exp.basicFunction().basicName().getText() === 'match' || exp.basicFunction().basicName().getText() === 'patternMatch')) {
                return exp;
            }
        }
        return null;
    }

    private static _getEachFnLevel(eachFn: any): string {
        const matchFn = eachFn.expression();
        return matchFn.length > 1 && matchFn[0] ? matchFn[0].getText() : '';
    }

    private static _getColumnNameExpression(eachFn: any): string {
        const expressions = eachFn.expression();
        const expression = expressions[0].basicFunction ? expressions[1] : expressions[2];
        return expression ? expression.getText() : dataFlowPatternParameter;
    }
}
